import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiGldObservationsListRequest, Observation } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

type GLDObservationRequestWithGld = ApiGldObservationsListRequest & { gld: string };

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getGldObservationSingleQuery = (uuid: string) => ({
  queryKey: ["gld-observation-single", uuid],
  queryFn: () => uploadApi.apiGldObservationsRead({ uuid }),
});

export const useGldObservationSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<Observation>(
      queryClient,
      ["gld-observation"],
      uuid,
      getGldObservationSingleQuery(uuid)
    );
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGldObservationListQuery = ({ gld, ...options }: GLDObservationRequestWithGld) => ({
  queryKey: ["gld-observation", gld, simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGldObservationsList({ gld, ...options }),
});

export const useGldObservationListQuery = (options: GLDObservationRequestWithGld) => {
  return useQuery(getGldObservationListQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGldObservationQueries = () => {
  const queryClient = useQueryClient();
  return async (gld: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["gld-observation-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGldObservationListQuery({ gld }).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};
